import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

const API_URL = '/cargo/api/company';

export type CompanyRole = 'COMPANY_ADMIN' | 'COMPANY_MANAGER' | 'COMPANY_OPERATOR';

export type CompanyPermission =
  | 'MANAGE_MEMBERS'
  | 'MANAGE_COMPANY'
  | 'MANAGE_REQUESTS'
  | 'PLACE_BIDS'
  | 'ASSIGN_REQUESTS'
  | 'ASSIGN_DEALS'
  | 'VIEW_DEALS'
  | 'WORK_DEALS'
  | 'CHAT'
  | 'VIEW_AMOUNTS';

export interface CompanyInfo {
  id: number;
  type: 'FORWARDER' | 'CARGO_OWNER' | 'CUSTOMS_BROKER' | 'SUPPLIER';
  name: string;
  bin?: string;
  countryId?: number;
  myRole?: CompanyRole;
  myPermissions: CompanyPermission[];
}

export interface CompanyMember {
  id: number;
  fullName: string;
  email: string;
  phone?: string;
  companyRole: CompanyRole;
  createdAt?: string;
}

/** Сотрудник для выбора исполнителя на запрос (менеджер) или сделку (оператор). */
export interface CompanyAssignee {
  id: number;
  fullName: string;
  companyRole: CompanyRole;
}

export interface UpdateMemberRequest {
  fullName: string;
  email: string;
  phone?: string;
  companyRole: CompanyRole;
}

export interface CreateMemberRequest {
  fullName: string;
  email: string;
  phone?: string;
  password: string;
  companyRole: CompanyRole;
}

@Injectable({ providedIn: 'root' })
export class CompanyService {
  constructor(private http: HttpClient) {}

  getMyCompany(): Observable<CompanyInfo> {
    return this.http.get<CompanyInfo>(`${API_URL}/me`);
  }

  getMembers(): Observable<CompanyMember[]> {
    return this.http.get<CompanyMember[]>(`${API_URL}/members`);
  }

  changeRole(userId: number, companyRole: CompanyRole): Observable<CompanyMember> {
    return this.http.patch<CompanyMember>(`${API_URL}/members/${userId}`, { companyRole });
  }

  removeMember(userId: number): Observable<void> {
    return this.http.delete<void>(`${API_URL}/members/${userId}`);
  }

  createMember(request: CreateMemberRequest): Observable<CompanyMember> {
    return this.http.post<CompanyMember>(`${API_URL}/members`, request);
  }

  updateMember(userId: number, request: UpdateMemberRequest): Observable<CompanyMember> {
    return this.http.put<CompanyMember>(`${API_URL}/members/${userId}`, request);
  }

  /** Менеджеры компании: на них администратор закрепляет запросы. */
  getManagers(): Observable<CompanyAssignee[]> {
    return this.http.get<CompanyAssignee[]>(`${API_URL}/assignees/managers`);
  }

  /** Операторы компании: за ними администратор и менеджер закрепляют сделки. */
  getOperators(): Observable<CompanyAssignee[]> {
    return this.http.get<CompanyAssignee[]>(`${API_URL}/assignees/operators`);
  }

  /** Закрепить запрос за менеджером (userId = null снимает закрепление). */
  assignRequestManager(requestId: number, userId: number | null): Observable<unknown> {
    return this.http.put(`/cargo/api/cargo-requests/${requestId}/manager`, { userId });
  }

  /** Закрепить запрос за менеджером своей компании-экспедитора (userId = null снимает закрепление). */
  assignForwarderManager(requestId: number, userId: number | null): Observable<unknown> {
    return this.http.put(`/cargo/api/cargo-requests/${requestId}/forwarder-manager`, { userId });
  }

  /** Закрепить сделку за оператором (userId = null снимает закрепление). */
  assignDealOperator(dealId: number, userId: number | null): Observable<unknown> {
    return this.http.put(`/cargo/api/deals/${dealId}/operator`, { userId });
  }

  resetPassword(userId: number, password: string): Observable<void> {
    return this.http.put<void>(`${API_URL}/members/${userId}/password`, { password });
  }
}
